import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { Clock, CheckCircle2, XCircle, ShoppingCart, Pencil, Paperclip } from "lucide-react";
import Attachments from "../components/Attachments";

// France UAT 9-21: Lead Time Inquiry (to Purchasing) and ETA & QTY Verification (to Inquiry Dept).
// Raised from the Inquiry screen when an item is out of stock; the department fills the details,
// the required roles approve, and the requester adds the item to the Sales Cart.

const num = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function InquiryRequests() {
    const { user } = useAuth();
    const [tab, setTab] = useState("lead_time");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fill, setFill] = useState(null); // the request being fulfilled
    const [attachFor, setAttachFor] = useState(null); // request whose deposit slip is being viewed/attached
    const [fStatus, setFStatus] = useState("");
    const [fBranch, setFBranch] = useState("");

    const role = user?.role;
    const isAdmin = role === "admin";
    const isPD = isAdmin || role === "purchasing";
    const isID = isAdmin || role === "inquiry_dept";
    const isTD = isAdmin || role === "treasury";

    const load = async () => {
        setLoading(true);
        try { setRows((await api.get("/inquiry-requests")).data || []); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast.error(formatApiError(e)); } };
    const approve = (r) => act(async () => { await api.post(`/inquiry-requests/${r.id}/approve`); toast.success("Approved"); });
    const reject = (r) => act(async () => {
        const reason = window.prompt("Reason for rejection (optional):", "");
        if (reason === null) return;
        await api.post(`/inquiry-requests/${r.id}/reject`, { reason }); toast.success("Rejected");
    });
    const addToCart = (r) => act(async () => { await api.post(`/inquiry-requests/${r.id}/add-to-cart`); toast.success("Added to Sales Cart"); });

    const kindRows = rows.filter((r) => r.kind === tab);
    const statusOpts = [...new Set(kindRows.map((r) => r.status).filter(Boolean))];
    const branchOpts = [...new Set(kindRows.map((r) => r.branch).filter(Boolean))];
    const list = kindRows.filter((r) => (!fStatus || r.status === fStatus) && (!fBranch || (r.branch || "") === fBranch));
    const isLead = tab === "lead_time";
    const isOwner = (r) => r.user_email === user?.email;
    const canFill = (r) => (isLead ? isPD : isID) && (r.status === "submitted_to_pd" || r.status === "submitted_to_id");
    const canApproveID = (r) => !isLead && isID && r.status === "updated_to_store" && !(r.approvals || {}).inquiry_dept;
    const canReject = (r) => (isLead ? (isPD || isTD) : isID) && !["approved", "carted", "rejected"].includes(r.status);
    // France 9-22: BOTH kinds get Add to Cart once the department has REVIEWED (replied), i.e. at
    // "updated_to_store" -- Lead Time once PD replies, ETA/Qty once ID replies. The PD+Treasury
    // approval still runs but no longer gates the cart step. The backend add-to-cart guard allows
    // the same two statuses (approved / updated_to_store) for both kinds.
    // A Lead Time reply without a PO Cut-off Date is not a real answer yet (Sir Lynx 2026-09-30) --
    // the store must wait for Purchasing to actually provide one before Add to Cart appears.
    const _todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
    const _cutoffOk = (r) => { const c = String(r.po_cutoff_date || "").slice(0, 10); if (!c) return false; return !/^\d{4}-\d{2}-\d{2}$/.test(c) || c >= _todayStr; };
    const _idOk = (r) => String(r.confirmation || "").toUpperCase() !== "NOT OK";
    const canCart = (r) => isOwner(r) && (r.status === "approved" || r.status === "updated_to_store")
        && (r.kind !== "lead_time" || _cutoffOk(r))
        && (r.kind !== "eta_qty" || _idOk(r));
    const cartBlockedReason = (r) => {
        if (!isOwner(r) || !(r.status === "approved" || r.status === "updated_to_store")) return "";
        if (r.kind === "eta_qty" && !_idOk(r)) return "ID replied NOT OK";
        if (r.kind === "lead_time" && !_cutoffOk(r)) return "PO cut-off passed";
        return "";
    };

    return (
        <div className="p-4 md:p-6 space-y-4" data-testid="inquiry-requests-page">
            <div>
                <div className="sc-section-title">Inquiry</div>
                <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Clock className="w-5 h-5" /> Lead Time / ETA Requests</h1>
                <p className="text-sm text-muted-foreground mt-1">Out-of-stock requests. Lead Time Inquiry - PD goes to Purchasing; Lead Time Inquiry - ID goes to the Inquiry Department. Once the department replies, the requester adds it to the Sales Cart.</p>
            </div>

            <div className="inline-flex rounded-md border border-border overflow-hidden">
                {[["lead_time", "Lead Time Inquiry - PD"], ["eta_qty", "Lead Time Inquiry - ID"]].map(([k, l], i) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-3 py-1.5 text-sm ${i > 0 ? "border-l border-border" : ""} ${tab === k ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}>
                        {l} <span className="text-[10px] text-muted-foreground">({rows.filter((r) => r.kind === k).length})</span>
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2 text-xs flex-wrap">
                <span className="text-muted-foreground">Filter:</span>
                <select className="sc-input h-8 text-xs" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="ir-filter-status">
                    <option value="">All statuses</option>
                    {statusOpts.map((st) => <option key={st} value={st}>{String(st).replace(/_/g, " ")}</option>)}
                </select>
                <select className="sc-input h-8 text-xs" value={fBranch} onChange={(e) => setFBranch(e.target.value)} data-testid="ir-filter-branch">
                    <option value="">All branches</option>
                    {branchOpts.map((br) => <option key={br} value={br}>{br}</option>)}
                </select>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1200px]">
                        <thead>
                            <tr>
                                <th>Doc</th><th>Store</th><th>Branch</th><th>Agent</th><th>Item</th><th className="text-right">Qty</th>
                                {isLead ? <><th>Mobile (Sales Agent)</th><th>Date Needed</th><th>Comments</th><th className="border-l-2 border-border">PO Cut-off</th></> : <><th>Comments</th></>}
                                <th>ETA</th>
                                {isLead ? <th>Down Pmt</th> : <><th className="border-l-2 border-border">Confirm</th><th>As of</th></>}
                                <th>Remarks</th>
                                <th>Import</th><th>Status</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={16} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && list.length === 0 && <tr><td colSpan={16} className="text-center py-8 text-muted-foreground">No requests.</td></tr>}
                            {list.map((r) => (
                                <tr key={r.id}>
                                    <td className="font-mono text-xs">{r.doc_no}</td>
                                    <td className="text-xs font-medium">{r.store_name || r.schema_name || "—"}</td>
                                    <td className="text-xs">{r.branch || "—"}</td>
                                    <td className="text-xs">{r.agent_name || r.user_email || "—"}</td>
                                    <td><div className="font-mono text-xs">{r.item_code}</div><div className="text-[11px] text-muted-foreground">{r.item_name} {r.size ? `· ${r.size}` : ""}</div></td>
                                    <td className="sc-num">{num(r.quantity)}<div className="text-[10px] text-muted-foreground">{r.bxs ? `${r.bxs} bx` : ""}{r.pcs ? ` ${r.pcs} pc` : ""}</div></td>
                                    {isLead ? <><td className="text-xs">{r.mobile_number || "—"}</td><td className="text-xs">{r.date_needed || "—"}</td><td className="text-xs text-muted-foreground max-w-[280px] whitespace-normal break-words align-top" title={r.comments || ""}>{r.comments || "—"}</td><td className="text-xs border-l-2 border-border">{r.po_cutoff_date || "—"}</td></> : <><td className="text-xs text-muted-foreground max-w-[280px] whitespace-normal break-words align-top">{r.comments || "—"}</td></>}
                                    <td className="text-xs">{r.eta || "—"}</td>
                                    {isLead ? <td className="text-xs">{r.down_payment || "—"}</td> : <><td className="text-xs border-l-2 border-border">{r.confirmation || "—"}</td><td className="text-[11px] text-muted-foreground">{r.as_of_datetime ? new Date(r.as_of_datetime).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td></>}
                                    <td className="text-xs text-muted-foreground max-w-[280px] whitespace-normal break-words align-top" title={r.remarks || ""}>{r.remarks || "—"}</td>
                                    <td className="text-xs">
                                        {r.is_import
                                            ? <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-semibold">Import</span>
                                            : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    <td><StatusBadge status={r.status} /></td>
                                    <td>
                                        <div className="flex items-center gap-1.5 justify-end">
                                            {canFill(r) && <BtnI onClick={() => setFill(r)} icon={Pencil}>Fill</BtnI>}
                                            {canApproveID(r) && <BtnI onClick={() => approve(r)} icon={CheckCircle2}>Approve (ID)</BtnI>}
                                            {canReject(r) && <BtnI onClick={() => reject(r)} icon={XCircle} danger>Reject</BtnI>}
                                            {canCart(r) && <BtnI onClick={() => addToCart(r)} icon={ShoppingCart}>Add to Cart</BtnI>}
                                            {!canCart(r) && cartBlockedReason(r) && <span className="text-[10px] text-rose-600 font-medium self-center" title="Add to Cart is blocked">{cartBlockedReason(r)}</span>}
                                            {isLead && isOwner(r) && <BtnI onClick={() => setAttachFor(r)} icon={Paperclip}>Deposit Slip{(r.attachments || []).length ? ` (${r.attachments.length})` : ""}</BtnI>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {fill && <FillModal r={fill} onClose={() => setFill(null)} onDone={async () => { setFill(null); await load(); }} />}
            {attachFor && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setAttachFor(null)}>
                    <div className="bg-background rounded-lg border border-border w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <div className="font-semibold">Deposit Slip — {attachFor.doc_no}</div>
                            <button className="text-sm text-muted-foreground" onClick={() => setAttachFor(null)}>Close</button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Attach proof of the down payment here. Treasury reviews this before approving the Lead Time Inquiry.</p>
                        <Attachments collection="inquiry-requests" docId={attachFor.id} editable
                            kinds={[["card_deposit_slip", "Deposit slip"], ["bdo_qr_ref", "BDO QR PH ref"], ["gcash_ref", "G-Cash ref"], ["other", "Other"]]}
                            defaultKind="card_deposit_slip" />
                    </div>
                </div>
            )}
        </div>
    );
}

function BtnI({ onClick, icon: Icon, children, danger }) {
    return (
        <button onClick={onClick} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${danger ? "bg-rose-50 text-rose-700 hover:bg-rose-100" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
            <Icon className="w-3.5 h-3.5" /> {children}
        </button>
    );
}

// Module-scope on purpose: defined INSIDE FillModal, this was recreated as a new function
// (a new component type, as far as React is concerned) on every keystroke -- so React
// unmounted and remounted the <input> DOM node each time instead of updating it, dropping
// focus after every letter. Hoisting it here keeps the same DOM node across renders.
function Fld({ label, k, type = "text", ph, value, onChange }) {
    return (
        <div>
            <label className="text-xs text-muted-foreground">{label}</label>
            <input type={type} className="sc-input mt-1 h-9" value={value} placeholder={ph} onChange={(e) => onChange(k, e.target.value)} />
        </div>
    );
}

function FillModal({ r, onClose, onDone }) {
    const isLead = r.kind === "lead_time";
    const [f, setF] = useState(isLead
        ? { po_cutoff_date: r.po_cutoff_date || "", eta: r.eta || "", down_payment: r.down_payment || "", remarks: r.remarks || "" }
        : { eta: r.eta || "", confirmation: r.confirmation || "OK", remarks: r.remarks || "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const submit = async () => {
        setBusy(true);
        try { await api.put(`/inquiry-requests/${r.id}/fulfill`, f); toast.success("Sent back to the store"); await onDone(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-background rounded-lg border border-border w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="font-semibold">{isLead ? "Lead Time Inquiry — Purchasing" : "ETA & QTY Verification — Inquiry Dept"}</div>
                    <button className="text-sm text-muted-foreground" onClick={onClose}>Close</button>
                </div>
                <div className="text-xs text-muted-foreground font-mono">{r.doc_no} · {r.item_code} · {r.item_name} · qty {num(r.quantity)}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {isLead ? <>
                        <Fld label="PO Cut-off Date" k="po_cutoff_date" type="date" value={f.po_cutoff_date} onChange={set} />
                        <Fld label="ETA" k="eta" type="date" value={f.eta} onChange={set} />
                        <Fld label="Down Payment" k="down_payment" ph="e.g. 30% (regular)" value={f.down_payment} onChange={set} />
                        <Fld label="Remarks" k="remarks" value={f.remarks} onChange={set} />
                    </> : <>
                        <Fld label="ETA" k="eta" type="date" value={f.eta} onChange={set} />
                        <div>
                            <label className="text-xs text-muted-foreground">Confirmation</label>
                            <select className="sc-input mt-1 h-9" value={f.confirmation} onChange={(e) => set("confirmation", e.target.value)}>
                                <option value="OK">OK</option>
                                <option value="NOT OK">NOT OK</option>
                            </select>
                        </div>
                        <div className="sm:col-span-2"><Fld label="Remarks" k="remarks" value={f.remarks} onChange={set} /></div>
                    </>}
                </div>
                {!isLead && <p className="text-[11px] text-muted-foreground">The checking is based on the information available at the time of verification and requires reconfirmation before processing in SAP.</p>}
                {isLead && <p className="text-[11px] text-muted-foreground">Needs approval of Purchasing and Treasury. If it misses the PO cut-off date, the store must run a Lead Time Inquiry again.</p>}
                <div className="flex justify-end">
                    <button className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60" disabled={busy} onClick={submit}>{busy ? "Saving…" : "Update to Store"}</button>
                </div>
            </div>
        </div>
    );
}
